import { ConversationSidebar } from 'components/Conversations/ConversationSidebar';
import { ConversationColumnVisibility } from 'components/Conversations/ConversationColumnVisibility';
import { Conversation } from '@/types';
import { ConversationsActionCell } from 'components/Conversations/ConversationsActionCell';
import { ConversationDataFilter } from 'components/Conversations/ConversationDataFilter';
import { EmptyTableButton } from 'components/Conversations/EmptyTableButton';
import SearchBar from 'components/SearchBar';
import { Button } from 'components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/shadcn/dialog';
import { Skeleton } from 'components/shadcn/skeleton';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/shadcn/table';
import dayjs from 'dayjs';
import { useConversationsList } from '@/hooks/useConversationsList';
import { OrderBy, useConversationsQueryParams } from '@/hooks/useConversationsQueryParams';
import { ArrowUpDown, Info } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/shadcn/tooltip';
import { apiUrl, cn, fetchWithAuth } from 'utils';

const Conversations = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedMetadata, setSelectedMetadata] = useState<unknown>(null);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const limit = 50;
  const [extractedDataKeys, setExtractedDataKeys] = useState<string[]>([]);
  const [metadataKeys, setMetadataKeys] = useState<string[]>([]);
  const [filterKeys, setFilterKeys] = useState<string[]>([]);
  const [hiddenColumnIds, setHiddenColumnIds] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const { stringifyURLParams, setSearch, setDataFilters, params, setPage, setSort } =
    useConversationsQueryParams(limit);
  const { page, search, sortDir, sortBy, filters } = params;
  const columnVisibilityStorageKey = `conversations-column-visibility:${id ?? 'unknown'}`;
  const columnOrderStorageKey = `conversations-column-order:${id ?? 'unknown'}`;

  useEffect(() => {
    try {
      const storedHiddenColumns = window.localStorage.getItem(columnVisibilityStorageKey);
      const parsedHiddenColumns = storedHiddenColumns ? JSON.parse(storedHiddenColumns) : [];
      setHiddenColumnIds(Array.isArray(parsedHiddenColumns) ? parsedHiddenColumns : []);
    } catch {
      setHiddenColumnIds([]);
    }
  }, [columnVisibilityStorageKey]);

  useEffect(() => {
    try {
      const storedColumnOrder = window.localStorage.getItem(columnOrderStorageKey);
      const parsedColumnOrder = storedColumnOrder ? JSON.parse(storedColumnOrder) : [];
      setColumnOrder(Array.isArray(parsedColumnOrder) ? parsedColumnOrder : []);
    } catch {
      setColumnOrder([]);
    }
  }, [columnOrderStorageKey]);

  const toggleColumn = (columnId: string, checked: boolean) => {
    const next = checked
      ? hiddenColumnIds.filter((id) => id !== columnId)
      : [...hiddenColumnIds, columnId];

    setHiddenColumnIds(next);
    try {
      window.localStorage.setItem(columnVisibilityStorageKey, JSON.stringify(next));
    } catch {
      // Column visibility still works for the current session if storage is unavailable.
    }
  };

  const reorderColumns = (draggedColumnId: string, targetColumnId: string) => {
    const currentOrder = orderedColumnOptions.map(({ id }) => id);
    const draggedIndex = currentOrder.indexOf(draggedColumnId);
    const targetIndex = currentOrder.indexOf(targetColumnId);
    if (draggedIndex < 0 || targetIndex < 0) return;

    const nextOrder = [...currentOrder];
    nextOrder.splice(draggedIndex, 1);
    const insertionIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
    nextOrder.splice(insertionIndex, 0, draggedColumnId);
    setColumnOrder(nextOrder);
    try {
      window.localStorage.setItem(columnOrderStorageKey, JSON.stringify(nextOrder));
    } catch {
      // Column order still works for the current session if storage is unavailable.
    }
  };

  const fetchConversations = useCallback(async () => {
    if (!id) {
      throw new Error('Conversation missing id');
    }

    let url = `${apiUrl}/conversation/${id}`;

    url += stringifyURLParams();

    const response = await fetchWithAuth(url);
    const data = await response.json();

    return data;
  }, [id, page, search, sortDir, sortBy, filters]);

  const { isFetching, isError, isSuccess, data } = useConversationsList(
    id || '',
    params,
    fetchConversations,
  );

  const columnOptions: { id: string; label: string; description?: string }[] = [
    { id: 'contact', label: t('dashboard.conversations.table.contact') },
    { id: 'messages', label: t('dashboard.conversations.table.messages') },
    { id: 'updatedAt', label: t('dashboard.conversations.table.lastMessageTime') },
    ...metadataKeys.map((key) => ({
      id: `metadata:${key}`,
      label: key,
      description: data?.metadataFields?.find((field) => field.name === key)?.description,
    })),
    ...extractedDataKeys.map((key) => ({
      id: `extractedData:${key}`,
      label: key,
      description: data?.extractedDataFields?.find((field) => field.name === key)?.description,
    })),
  ];

  const orderedColumnOptions = [
    ...columnOrder
      .map((id) => columnOptions.find((column) => column.id === id))
      .filter((column): column is (typeof columnOptions)[number] => Boolean(column)),
    ...columnOptions.filter(({ id }) => !columnOrder.includes(id)),
  ];

  const sidebarFieldNames = Array.from(
    new Set(
      orderedColumnOptions.flatMap(({ id }) => {
        if (id.startsWith('metadata:')) return [id.slice('metadata:'.length)];
        if (id.startsWith('extractedData:')) return [id.slice('extractedData:'.length)];
        return [];
      }),
    ),
  );

  const isColumnVisible = (columnId: string) => !hiddenColumnIds.includes(columnId);
  const visibleColumnOptions = orderedColumnOptions.filter(({ id }) => isColumnVisible(id));

  useEffect(() => {
    if (!data) return;

    const metadataKeysSet = new Set<string>();
    const extractedDataKeysSet = new Set<string>();

    for (const conversation of data.conversations) {
      if (conversation.metadata && !Array.isArray(conversation.metadata)) {
        Object.keys(conversation.metadata).forEach((key) => metadataKeysSet.add(key));
      }
      if (conversation.extractedData && !Array.isArray(conversation.extractedData)) {
        Object.keys(conversation.extractedData).forEach((key) => extractedDataKeysSet.add(key));
      }
    }

    const configuredMetadataKeys = (data.metadataFields ?? [])
      .map(({ name }) => name)
      .filter(Boolean);
    const configuredExtractedDataKeys = (data.extractedDataFields ?? [])
      .map(({ name }) => name)
      .filter(Boolean);

    setMetadataKeys(configuredMetadataKeys);
    setExtractedDataKeys(configuredExtractedDataKeys);
    setFilterKeys(
      Array.from(
        new Set([
          ...(data.dataKeys ?? []),
          ...configuredMetadataKeys,
          ...configuredExtractedDataKeys,
          ...metadataKeysSet,
          ...extractedDataKeysSet,
        ]),
      ).sort(),
    );
  }, [data]);

  // TODO: better error page
  if (isError) {
    return <h1 className="p-4 text-balance">{t('errors.generalError')}</h1>;
  }

  const conversations = data ? data.conversations : undefined;
  const totalConversations = data ? data.totalConversations : 0;

  const totalPages = Math.ceil(totalConversations / limit);

  const getPageNumbers: () => (number | string)[] = () => {
    const pageNumbers = [];
    const maxPageButtons = 10;
    const halfRange = Math.floor(maxPageButtons / 2);
    let startPage = Math.max(2, page - halfRange);
    let endPage = Math.min(totalPages - 1, page + halfRange);

    if (page <= halfRange) {
      endPage = Math.min(totalPages - 1, maxPageButtons);
    }
    if (page + halfRange >= totalPages) {
      startPage = Math.max(2, totalPages - maxPageButtons + 1);
    }

    if (startPage > 2) {
      pageNumbers.push('start-ellipsis');
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages - 1) {
      pageNumbers.push('end-ellipsis');
    }

    return pageNumbers;
  };

  const handleNextPage = () => {
    if (page * limit < totalConversations) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const setOrFlipSort = (by: OrderBy) => {
    if (sortBy !== by) {
      setSort(sortDir, by);
    } else {
      const dir = sortDir === 'asc' ? 'desc' : 'asc';
      setSort(dir);
    }
  };

  const isNoResult = isSuccess && (!conversations || !conversations.length);

  const renderColumnHeader = ({
    id: columnId,
    label,
    description,
  }: (typeof columnOptions)[number]) => {
    const headerLabel = description ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            className="inline-flex items-center gap-1"
            aria-label={`${label}: ${description}`}
          >
            {label}
            <Info aria-hidden="true" size={14} />
          </span>
        </TooltipTrigger>
        <TooltipContent>{description}</TooltipContent>
      </Tooltip>
    ) : (
      label
    );

    if (columnId === 'messages') {
      return (
        <TableHead
          key={columnId}
          className="w-20 p-0"
          title={t('dashboard.conversations.numberOfMessagesTitle')}
        >
          <Button variant="ghost" onClick={() => setOrFlipSort('messages')}>
            {headerLabel}
            <ArrowUpDown size={15} className="ms-4" />
          </Button>
        </TableHead>
      );
    }

    if (columnId === 'updatedAt') {
      return (
        <TableHead key={columnId} className="p-0">
          <Button variant="ghost" onClick={() => setOrFlipSort('updatedAt')}>
            {headerLabel}
            <ArrowUpDown size={15} className="ms-4" />
          </Button>
        </TableHead>
      );
    }

    return (
      <TableHead key={columnId} className={columnId === 'contact' ? 'w-96' : undefined}>
        {headerLabel}
      </TableHead>
    );
  };

  const renderColumnCell = (conversation: Conversation, columnId: string) => {
    if (columnId === 'contact')
      return <TableCell key={columnId}>{conversation.username}</TableCell>;
    if (columnId === 'messages') {
      return <TableCell key={columnId}>{conversation.messages.length}</TableCell>;
    }
    if (columnId === 'updatedAt') {
      return (
        <TableCell key={columnId}>
          {dayjs(
            conversation.messages[Math.max(0, conversation.messages.length - 1)]?.createdAt,
          ).format('DD/MM/YYYY HH:mm')}
        </TableCell>
      );
    }

    const isMetadataColumn = columnId.startsWith('metadata:');
    const field = columnId.slice(isMetadataColumn ? 'metadata:'.length : 'extractedData:'.length);
    const values = isMetadataColumn ? conversation.metadata : conversation.extractedData;
    const value =
      values && typeof values === 'object' && !Array.isArray(values) ? values[field] : undefined;

    return <TableCell key={columnId}>{value !== undefined ? String(value) : ''}</TableCell>;
  };

  return (
    <div className="flex w-full items-start gap-1 relative min-h-screen">
      <div className="flex-1 min-w-0 mx-auto py-6 px-4 lg:px-6 box-border animate-in fade-in duration-500">
        <div className="my-4 flex flex-wrap items-center justify-between gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-backwards delay-0">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <SearchBar
              placeholder={t('common.searchPlaceholder')}
              value={search}
              onChange={setSearch}
              className="max-w-sm shadow-sm transition-[box-shadow,ring] duration-200 focus-within:shadow-md focus-within:ring-1 focus-within:ring-primary"
            />
            <ConversationDataFilter
              dataKeys={filterKeys}
              filters={filters}
              onApply={(key, value) => setDataFilters([...filters, { key, value }])}
              onRemove={(index) =>
                setDataFilters(filters.filter((_, filterIndex) => filterIndex !== index))
              }
              onClear={() => setDataFilters([])}
            />
          </div>
          <ConversationColumnVisibility
            columns={orderedColumnOptions}
            isColumnVisible={isColumnVisible}
            onColumnVisibilityChange={toggleColumn}
            onColumnOrderChange={reorderColumns}
          />
        </div>
        <div className="rounded-md border bg-card shadow-sm transition-shadow hover:shadow-md animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-backwards delay-100">
          <Table>
            <TableHeader>
              <TableRow>
                {visibleColumnOptions.map(renderColumnHeader)}
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isFetching &&
                Array.from({ length: limit }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-12" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                  </TableRow>
                ))}
              {!isNoResult &&
                conversations?.map((conversation: Conversation) => {
                  const isActive =
                    activeConversation &&
                    activeConversation.chatId === conversation.chatId &&
                    activeConversation.username === conversation.username;

                  return (
                    <TableRow
                      key={`${conversation.chatId} + ${conversation.username}`}
                      className={cn(
                        'transition-colors duration-200 relative cursor-pointer',
                        isActive && 'bg-muted/80 hover:bg-muted/90 font-medium',
                      )}
                      onClick={() => setActiveConversation(isActive ? null : conversation)}
                    >
                      {visibleColumnOptions.map(({ id: columnId }) =>
                        renderColumnCell(conversation, columnId),
                      )}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <ConversationsActionCell
                          conversation={conversation}
                          onViewConversations={() => setActiveConversation(conversation)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          {isNoResult &&
            (search ? (
              <EmptyTableButton
                headerText={t('dashboard.conversations.empty.searchHeader')}
                buttonText={t('dashboard.conversations.empty.clearSearch')}
                onClick={() => {
                  setSearch('');
                }}
              />
            ) : (
              <EmptyTableButton
                headerText={t('dashboard.conversations.empty.noConversationsHeader')}
                buttonText={t('dashboard.conversations.empty.chatWithBot')}
                onClick={() => {
                  navigate(`/${id}`);
                }}
              />
            ))}
        </div>

        {!isNoResult && (
          <div className="flex justify-between mt-4">
            <Button variant="outline" onClick={handlePreviousPage} disabled={page === 1}>
              {t('common.previous')}
            </Button>

            <div className="flex justify-center grow gap-2">
              <Button variant={page === 1 ? 'secondary' : 'outline'} onClick={() => setPage(1)}>
                1
              </Button>
              {getPageNumbers().map((pageNumber, index) =>
                typeof pageNumber === 'string' ? (
                  <span key={index}>&hellip;</span>
                ) : (
                  <Button
                    key={pageNumber}
                    variant={pageNumber === page ? 'secondary' : 'outline'}
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber}
                  </Button>
                ),
              )}

              {totalPages > 1 && (
                <Button
                  variant={page === totalPages ? 'secondary' : 'outline'}
                  onClick={() => setPage(totalPages)}
                >
                  {totalPages}
                </Button>
              )}
            </div>

            <Button variant="outline" onClick={handleNextPage} disabled={page === totalPages}>
              {t('common.next')}
            </Button>
          </div>
        )}

        <Dialog open={!!selectedMetadata} onOpenChange={() => setSelectedMetadata(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('dashboard.conversations.dialogs.metadataTitle')}</DialogTitle>
              <DialogDescription>
                <pre>{JSON.stringify(selectedMetadata, null, 2)}</pre>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedMetadata(null)}>
                {t('ui.close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Secondary Sidebar */}
      <ConversationSidebar
        conversation={activeConversation}
        fieldNames={sidebarFieldNames}
        onClose={() => setActiveConversation(null)}
      />
    </div>
  );
};

export default Conversations;
